import React from "react";
import { FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { PLATFORM_CATEGORIES, PLATFORM_EXTRA_CONFIG_SCHEMA, isCategoryMissing } from "../../Enums/platform_constants";

/**
 * The tab's platform setting that Step 3's Platform Settings shows first:
 *  - Channel / Category (`config.category`), required where the platform has
 *    categories (YouTube, MSN, Dailymotion);
 *  - Facebook has no category but a Media Type — Video / Reels / Stories —
 *    stored as `config.extra_config.media_type` (default Video), exactly as
 *    Step 3 stores it.
 * Each platform keeps its own value, duplicates included, so the two screens
 * always agree. Renders nothing for a platform with neither.
 */
export default function CategoryControl({ item, onChange, onExtraChange, size = "header" }) {
  const header = size === "header";
  const categories = PLATFORM_CATEGORIES[item.platform];
  const mediaType = !categories && PLATFORM_EXTRA_CONFIG_SCHEMA[item.platform]?.media_type;
  if (!categories && !mediaType) return null;

  const missing = Boolean(categories) && isCategoryMissing(item.platform, item.config);
  const extra = item.config?.extra_config || {};
  const options = categories || mediaType.options;
  const value = categories ? item.config?.category || "" : extra.media_type ?? mediaType.default;
  const prefix = categories ? "Category" : "Type";
  const change = (v) => (categories ? onChange(v) : onExtraChange({ ...extra, media_type: v }));
  const tip = categories
    ? missing
      ? `Choose a ${item.platform} category — required to publish`
      : `${item.platform} category`
    : `${item.platform} media type — Video, Reels or Stories`;

  return (
    <Tooltip title={tip} disableInteractive>
      <FormControl size="small" error={missing} sx={{ minWidth: header ? 150 : 220 }}>
        <Select
          value={value}
          displayEmpty
          onChange={(e) => change(e.target.value)}
          inputProps={{
            "aria-label": `${item.platform} ${categories ? "channel / category" : "media type"}`,
            "data-testid": categories ? "batch-category" : "batch-media-type",
          }}
          renderValue={(v) => (v ? `${prefix}: ${options.find((o) => o.value === v)?.label || v}` : `${prefix} *`)}
          sx={{
            height: header ? 26 : 36,
            fontSize: header ? 11.5 : 13,
            fontWeight: 600,
            borderRadius: 1.5,
            color: missing ? "#b91c1c" : "#334155",
            bgcolor: missing ? "#fef2f2" : "white",
            "& .MuiSelect-select": { py: 0.25 },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: missing ? "#ef4444" : "#e2e8f0" },
          }}
        >
          {categories && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
}
